import { Percent } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import styled from 'styled-components';

import { Button } from '../../../../../../components/atoms/button/button';
import { useAuth } from '../../../../../../hooks/use-auth';
import { useClienteTaxas } from '../../../../../../hooks/use-cliente-taxas';
import {
  ClienteTaxa,
  TEMPO_RECEBIMENTO_LABEL,
  TEMPO_RECEBIMENTO_ORDER,
} from '../../../../../../types/cliente-taxa';
import { EditarTaxasDrawer } from './editar-taxas-drawer/editar-taxas-drawer';
import { formatPct } from './editar-taxas-drawer/format';

interface Props {
  clienteId: string;
  clienteNome: string;
}

const Card = styled.section`
  background: ${({ theme }) => theme.colors.bgSurface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  grid-column: 1 / -1;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textMuted};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Warning = styled.p`
  margin: 0;
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-style: italic;
`;

const Lines = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Line = styled.li`
  display: grid;
  grid-template-columns: 64px 1fr;
  gap: 12px;
  align-items: center;
  font-size: 13.5px;
  padding: 6px 8px;
  border-radius: ${({ theme }) => theme.radius.sm};

  &:nth-child(odd) {
    background: ${({ theme }) => theme.colors.bgMuted};
  }

  strong {
    font-weight: 700;
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  span {
    color: ${({ theme }) => theme.colors.textSecondary};
    font-variant-numeric: tabular-nums;
  }
`;

const Skeleton = styled.div`
  height: 28px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.bgMuted} 0%,
    ${({ theme }) => theme.colors.bgSurface} 50%,
    ${({ theme }) => theme.colors.bgMuted} 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s linear infinite;

  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  & + & {
    margin-top: 6px;
  }
`;

const Error = styled.div`
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.dangerBg};
  border-left: 3px solid ${({ theme }) => theme.colors.danger};
  color: ${({ theme }) => theme.colors.danger};
  font-size: 13px;
`;

const orderTaxas = (items: ClienteTaxa[]): ClienteTaxa[] => {
  const byRec = new Map(items.map((t) => [t.recebimento, t]));
  return TEMPO_RECEBIMENTO_ORDER.map((r) => byRec.get(r)).filter(
    (t): t is ClienteTaxa => t != null,
  );
};

export const TaxasComerciaisCard = ({ clienteId, clienteNome }: Props) => {
  const { hasPermission } = useAuth();
  const {
    items,
    status,
    error,
    mutating,
    mutationError,
    refresh,
    update,
    clearMutationError,
  } = useClienteTaxas(clienteId);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const canUpdate = hasPermission('CLIENTE_TAXA_UPDATE');
  const isLoading = status === 'loading';
  const hasError = status === 'error';
  const ordered = orderTaxas(items);

  const openDrawer = () => {
    clearMutationError();
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    if (mutating) return;
    setDrawerOpen(false);
    clearMutationError();
  };

  const handleSave = async (newTaxas: ClienteTaxa[]) => {
    const result = await update({ taxas: newTaxas });
    if (result && (result as { type: string }).type.endsWith('/fulfilled')) {
      toast.success('Taxas atualizadas');
      setDrawerOpen(false);
    } else {
      toast.error('Não foi possível salvar as taxas.');
    }
  };

  return (
    <Card data-testid="taxas-comerciais-card">
      <Header>
        <Title>
          <Percent size={14} /> Taxas comerciais
        </Title>
        {canUpdate && !isLoading && !hasError && (
          <Button
            variant="secondary"
            size="sm"
            onClick={openDrawer}
            data-testid="taxas-comerciais-editar"
          >
            Editar taxas
          </Button>
        )}
      </Header>

      <Warning>
        Alteração impacta os recebimentos futuros do cliente.
      </Warning>

      {isLoading && (
        <div data-testid="taxas-loading">
          <Skeleton />
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </div>
      )}

      {hasError && (
        <Error>
          {error ?? 'Não foi possível carregar as taxas.'}{' '}
          <Button variant="ghost" size="sm" onClick={() => refresh()}>
            Tentar novamente
          </Button>
        </Error>
      )}

      {!isLoading && !hasError && ordered.length > 0 && (
        <Lines>
          {ordered.map((t) => (
            <Line key={t.recebimento}>
              <strong>{TEMPO_RECEBIMENTO_LABEL[t.recebimento]}</strong>
              <span>
                Nac {formatPct(t.taxaNacional)} · Intl{' '}
                {formatPct(t.taxaInternacional)} · Saque{' '}
                {formatPct(t.taxaSaque)}
              </span>
            </Line>
          ))}
        </Lines>
      )}

      {drawerOpen && (
        <EditarTaxasDrawer
          isOpen={drawerOpen}
          clienteNome={clienteNome}
          taxas={ordered}
          saving={mutating}
          errorMessage={mutationError}
          onSave={handleSave}
          onClose={closeDrawer}
        />
      )}
    </Card>
  );
};
